import type { Polygon } from "geojson";
import { RegistroFincaBorradorLote } from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";

/** Una pieza del paso 5. `id` es local a esta sesión del paso (no persiste,
 *  se regenera cada vez que se entra); `tempId` es el id del lote en el
 *  registro temporal del servidor, el que se usa para guardar la
 *  asignación. `loteOrigenId` se preserva tal cual venía del borrador.
 *  `cultivoNombre` es el nombre del cultivo (productivo) o de la cobertura
 *  (no productivo), para el tooltip del mapa: se completa al aplicar, y para
 *  los lotes que ya venían asignados, cuando llegan los catálogos
 *  (`resolverNombres`). */
export type LoteAssignEntry = {
  id: number;
  tempId: string | null;
  nombreLote: string;
  polygon: Polygon;
  loteOrigenId: string | null;
  /** `null` = sin asignar todavía. */
  isAreaProductiva: boolean | null;
  cultivoId: string | null;
  coberturaId: string | null;
  cultivoNombre: string | null;
};

/** Estado compartido del paso 5 (mapa + barra de asignación lo leen/escriben
 *  por acá). A diferencia de `LoteSplitState`, no hay geometría que cambie —
 *  los lotes ya vienen cortados del paso 4; acá solo se selecciona
 *  (multi-select, click en el mapa) y se aplica área productiva + cultivo o
 *  cobertura en bloque a la selección.
 *
 *  **"Asignado" sale del dato, no de la sesión.** Un lote sin asignar tiene
 *  `isAreaProductiva === null`, distinto de `false` (asignado como no
 *  productivo). Antes el modelo no podía distinguirlos y el contador de
 *  "asignados" era un set en memoria que se vaciaba al volver a entrar al
 *  paso; ahora lo asignado en una visita anterior (o en otra sesión, al
 *  retomar el registro) se sigue viendo como asignado. */
export class LoteAssignState {
  private _entries: LoteAssignEntry[] = [];
  private _nextId = 1;
  private _selectedIds = new Set<number>();
  private _listeners: (() => void)[] = [];
  private _listenersAsignacion: ((entries: LoteAssignEntry[]) => void)[] = [];

  addOnChangeListener(handler: () => void): void {
    this._listeners.push(handler);
  }

  /** Se aplicó una asignación a estos lotes (para guardarla en el
   *  servidor). */
  addOnAsignacionAplicadaListener(
    handler: (entries: LoteAssignEntry[]) => void,
  ): void {
    this._listenersAsignacion.push(handler);
  }

  private _notifyChange(): void {
    this._listeners.forEach((handler) => handler());
  }

  init(lotes: readonly RegistroFincaBorradorLote[]): void {
    this._entries = lotes.map((lote) => ({
      id: this._nextId++,
      tempId: lote.id,
      nombreLote: lote.nombreLote,
      polygon: lote.areaGeograficaPolygon,
      loteOrigenId: lote.loteOrigenId,
      isAreaProductiva: lote.isAreaProductiva,
      cultivoId: lote.cultivoId,
      coberturaId: lote.coberturaId,
      cultivoNombre: null,
    }));
    this._selectedIds.clear();
    this._notifyChange();
  }

  getAll(): readonly LoteAssignEntry[] {
    return this._entries;
  }

  isSelected(id: number): boolean {
    return this._selectedIds.has(id);
  }

  toggleSelection(id: number): void {
    if (this._selectedIds.has(id)) this._selectedIds.delete(id);
    else this._selectedIds.add(id);
    this._notifyChange();
  }

  getSelectedIds(): readonly number[] {
    return [...this._selectedIds];
  }

  clearSelection(): void {
    if (this._selectedIds.size === 0) return;
    this._selectedIds.clear();
    this._notifyChange();
  }

  /** Cuántos lotes tienen asignación (`isAreaProductiva` no nulo). */
  getTocadosCount(): number {
    return this._entries.filter((entry) => entry.isAreaProductiva !== null)
      .length;
  }

  /** Si este lote tiene asignación. Lo usa el mapa para distinguir SIN
   *  ASIGNAR de "asignado como no productivo". */
  isTocado(id: number): boolean {
    const entry = this._entries.find((e) => e.id === id);
    return !!entry && entry.isAreaProductiva !== null;
  }

  getTotalCount(): number {
    return this._entries.length;
  }

  /** Asigna en memoria área productiva + el ítem de catálogo elegido a los
   *  lotes dados: `catalogoId` es un cultivo si el lote es productivo y una
   *  cobertura si no. Avisa a quien guarda en el servidor. */
  applyAssignment(
    ids: readonly number[],
    isAreaProductiva: boolean,
    catalogoId: string | null,
    catalogoNombre: string | null,
  ): void {
    const aplicados: LoteAssignEntry[] = [];
    ids.forEach((id) => {
      const entry = this._entries.find((e) => e.id === id);
      if (!entry) return;
      entry.isAreaProductiva = isAreaProductiva;
      entry.cultivoId = isAreaProductiva ? catalogoId : null;
      entry.coberturaId = isAreaProductiva ? null : catalogoId;
      entry.cultivoNombre = catalogoNombre;
      aplicados.push(entry);
    });
    this._selectedIds.clear();
    this._listenersAsignacion.forEach((handler) => handler(aplicados));
    this._notifyChange();
  }

  /** Completa el nombre de cultivo/cobertura de los lotes que ya venían
   *  asignados (el borrador solo guarda ids), para el tooltip del mapa. Se
   *  llama cuando llega cada catálogo. */
  resolverNombres(
    cultivos: readonly { id: string; nombre: string }[],
    coberturas: readonly { id: string; nombre: string }[],
  ): void {
    let cambio = false;
    this._entries.forEach((entry) => {
      if (entry.cultivoNombre) return;
      const nombre = entry.isAreaProductiva
        ? cultivos.find((c) => c.id === entry.cultivoId)?.nombre
        : coberturas.find((c) => c.id === entry.coberturaId)?.nombre;
      if (nombre) {
        entry.cultivoNombre = nombre;
        cambio = true;
      }
    });
    if (cambio) this._notifyChange();
  }
}
